import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader2, Save, ArrowLeft, Users, Smartphone, RefreshCw, ShieldCheck, UserCircle2, ScanLine } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Separator } from '../../../components/ui/separator';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import BarcodeScannerDialog from '../../../components/BarcodeScannerDialog';
import { findDeviceByScan } from '../../../utils/deviceScanMatch';

const normalizeAssignments = (rawAssignments = []) => {
  return (rawAssignments || [])
    .map((assignment) => {
      const device = assignment?.device || {};
      const account = assignment?.account || {};

      const assignmentId =
        assignment?._id ||
        assignment?.id ||
        assignment?.assignment_id ||
        assignment?.uuid ||
        null;

      const deviceId =
        assignment?.device_id ||
        assignment?.deviceId ||
        device?._id ||
        device?.id ||
        device?.uuid ||
        null;

      const accountId =
        assignment?.account_id ||
        assignment?.accountId ||
        account?._id ||
        account?.id ||
        account?.uuid ||
        null;

      const unassignedAt = assignment?.unassignedAt || assignment?.unassigned_at;
      const deletedAt = assignment?.deletedAt || assignment?.deleted_at;
      const deviceStatus = device?.deviceStatus || device?.device_status;

      return {
        assignmentId,
        deviceId,
        accountId,
        deviceName:
          device?.deviceName ||
          device?.device_name ||
          device?.name ||
          'Device',
        imei:
          device?.imeiNumber ||
          device?.imei_number ||
          device?.imei ||
          device?.imeiNo ||
          '',
        accountName: account?.name || account?.accountName || 'Account',
        accountEmail: account?.email || account?.accountEmail || '',
        assignedAt:
          assignment?.assignedAt ||
          assignment?.assigned_at ||
          assignment?.created_at ||
          assignment?.createdAt ||
          null,
        isActive:
          !unassignedAt &&
          !deletedAt &&
          (deviceStatus === 'Assigned' || deviceStatus === 'assigned'),
      };
    })
    .filter(
      (normalized) =>
        normalized.deviceId && normalized.accountId && normalized.isActive
    );
};

const normalizeCustomers = (rawCustomers = []) => {
  return (rawCustomers || [])
    .map((customer) => {
      const account = customer?.account || {};

      const id =
        customer?.customer_user_id ||
        customer?.customerUserId ||
        customer?._id ||
        customer?.id ||
        customer?.uuid ||
        null;

      const accountId =
        customer?.account_id ||
        customer?.accountId ||
        account?._id ||
        account?.id ||
        account?.uuid ||
        null;

      const firstName =
        customer?.first_name ||
        customer?.firstName ||
        customer?.firstname ||
        '';
      const lastName =
        customer?.last_name ||
        customer?.lastName ||
        customer?.lastname ||
        '';
      const name =
        customer?.name ||
        [firstName, lastName].filter(Boolean).join(' ').trim() ||
        customer?.email ||
        'Customer';

      return {
        id,
        accountId,
        name,
        email:
          customer?.email ||
          customer?.contact_email ||
          customer?.contactEmail ||
          '',
        phone:
          customer?.phone ||
          customer?.phone_number ||
          customer?.contact_number ||
          '',
      };
    })
    .filter((customer) => customer.id);
};

const AddCustomerDevices = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, post } = useApi();

  const [assignedDevices, setAssignedDevices] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [customerDeviceAssignments, setCustomerDeviceAssignments] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      device_id: '',
      account_id: '',
      customer_user_id: '',
    },
  });

  const watchedDeviceId = watch('device_id');
  const watchedAccountId = watch('account_id');
  const watchedCustomerUserId = watch('customer_user_id');

  // Get list of device IDs that are already assigned to customers
  const assignedDeviceIds = useMemo(() => {
    return new Set(
      customerDeviceAssignments
        .map((assignment) => {
          return (
            assignment?.device_id ||
            assignment?.deviceId ||
            assignment?.device?.device_id ||
            assignment?.device?._id ||
            assignment?.device?.id ||
            null
          );
        })
        .filter(Boolean)
    );
  }, [customerDeviceAssignments]);

  // Filter out devices that are already assigned to customers
  const availableDevices = useMemo(() => {
    return assignedDevices.filter(
      (assignment) => !assignedDeviceIds.has(assignment.deviceId)
    );
  }, [assignedDevices, assignedDeviceIds]);

  const selectedAssignment = useMemo(
    () =>
      availableDevices.find(
        (assignment) => assignment.deviceId === watchedDeviceId
      ),
    [availableDevices, watchedDeviceId]
  );

  const selectedCustomer = useMemo(
    () =>
      customerUsers.find((customer) => customer.id === watchedCustomerUserId) ||
      null,
    [customerUsers, watchedCustomerUserId]
  );

  useEffect(() => {
    fetchAssignedDevices();
    fetchCustomerUsers();
    fetchCustomerDeviceAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedAssignment?.accountId &&
      selectedAssignment.accountId !== watchedAccountId
    ) {
      setValue('account_id', selectedAssignment.accountId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (!selectedAssignment && watchedAccountId) {
      setValue('account_id', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [selectedAssignment, setValue, watchedAccountId]);

  const fetchAssignedDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const response = await get('/device-assignments?per_page=1000');
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setAssignedDevices(normalizeAssignments(data));
    } catch (error) {
      toast.error(
        error?.message || 'Failed to load assigned devices. Please try again.'
      );
      setAssignedDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const fetchCustomerUsers = async () => {
    setIsLoadingCustomers(true);
    try {
      const response = await get('/customer-list');
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setCustomerUsers(normalizeCustomers(data));
    } catch (error) {
      toast.error(
        error?.message || 'Failed to load customer users. Please try again.'
      );
      setCustomerUsers([]);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const fetchCustomerDeviceAssignments = async () => {
    try {
      const response = await get('/customer-device-assignments?active=1&per_page=1000');
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setCustomerDeviceAssignments(data);
    } catch (error) {
      // Don't show error toast for this, just log it
      setCustomerDeviceAssignments([]);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!selectedAssignment?.accountId) {
      return customerUsers;
    }

    const matches = customerUsers.filter(
      (customer) =>
        !customer.accountId || customer.accountId === selectedAssignment.accountId
    );

    return matches.length ? matches : customerUsers;
  }, [customerUsers, selectedAssignment]);

  const handleDeviceChange = (value) => {
    setValue('device_id', value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleCustomerChange = (value) => {
    setValue('customer_user_id', value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = async (formValues) => {
    if (!formValues.device_id || !formValues.account_id || !formValues.customer_user_id) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        device_id: formValues.device_id,
        account_id: formValues.account_id,
        customer_user_id: formValues.customer_user_id,
      };

      await post('/customer-device-assignments', payload);

      toast.success('Customer device assignment created successfully.');
      
      // Redirect to the customer device assignments list page
      navigate('/admin/customer-device-assignments');
    } catch (error) {
      toast.error(
        error?.message ||
          'Unable to create customer device assignment. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isLoadingDevices || isLoadingCustomers;
  const summaryDeviceName = selectedAssignment?.deviceName || 'Select a device';
  const summaryDeviceImei = selectedAssignment?.imei || 'Awaiting selection';
  const summaryAccountName =
    selectedAssignment?.accountName ||
    (watchedAccountId ? 'Selected account' : 'Awaiting selection');
  const summaryAccountEmail = selectedAssignment?.accountEmail || '';
  const summaryCustomerName =
    selectedCustomer?.name || 'Select a customer user';
  const summaryCustomerEmail = selectedCustomer?.email || '';
  const summaryCustomerPhone = selectedCustomer?.phone || '';
  const summaryAssignedDate = selectedAssignment?.assignedAt
    ? new Date(selectedAssignment.assignedAt).toLocaleString('en-GB')
    : 'Awaiting selection';
  const accountDisplayName =
    selectedAssignment?.accountName ||
    (watchedAccountId ? 'Selected account' : '');
  const accountDisplayEmail = selectedAssignment?.accountEmail || '';

  return (
    <div className="p-2 sm:p-6 lg:p-8 w-full space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-1 border-muted-foreground/20 rounded-lg p-4 bg-card shadow-sm">
        <div>
          <h1 className="text-xl  font-bold text-foreground">
            Assign Device to Customer
          </h1>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="border-1 border-muted-foreground/20 shadow-sm">
          <CardHeader className="space-y-2">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Smartphone className="w-5 h-5 " />
              Customer Device Assignment
            </CardTitle>
            <CardDescription>
              Choose an assigned device, confirm the linked account, then select the customer user.
            </CardDescription>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6 space-y-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-medium text-foreground">
                      Device <span className="text-destructive">*</span>
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsScannerOpen(true)}
                      disabled={isLoadingDevices || availableDevices.length === 0}
                    >
                      <ScanLine className="mr-1.5 h-3.5 w-3.5" />
                      Scan
                    </Button>
                  </div>
                  <Select
                    onValueChange={handleDeviceChange}
                    value={watchedDeviceId || undefined}
                    disabled={isLoadingDevices || availableDevices.length === 0}
                  >
                    <SelectTrigger className="w-full border-1 border-muted-foreground/20 bg-muted/40">
                      <SelectValue
                        placeholder={
                          isLoadingDevices
                            ? 'Loading devices...'
                            : availableDevices.length > 0
                              ? 'Select a device'
                              : 'No devices available'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="border-0 max-h-72">
                      {availableDevices.map((assignment) => (
                        <SelectItem
                          key={assignment.deviceId}
                          value={assignment.deviceId}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {assignment.deviceName}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    Account
                  </Label>
                  <div className="rounded-md border-1 border-muted-foreground/20 bg-muted/40 px-3 py-2 h-[42px] flex items-center text-sm text-muted-foreground">
                    {accountDisplayName || 'Select a device '}
                  </div>
                  <input
                    type="hidden"
                    {...register('account_id', { required: 'Account is required' })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    Customer User <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    onValueChange={handleCustomerChange}
                    value={watchedCustomerUserId || undefined}
                    disabled={
                      isLoadingCustomers ||
                      filteredCustomers.length === 0 ||
                      !selectedAssignment
                    }
                  >
                    <SelectTrigger className="w-full border-1 border-muted-foreground/20 bg-muted/40">
                      <SelectValue
                        placeholder={
                          !selectedAssignment
                            ? 'Select a device first'
                            : isLoadingCustomers
                              ? 'Loading customers...'
                              : filteredCustomers.length > 0
                                ? 'Select a customer'
                                : 'No customers available'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="border-0 max-h-72">
                      {filteredCustomers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          <div className="flex flex-col">
                            <span className="font-medium flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-muted-foreground" />
                              {customer.name}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.customer_user_id && (
                    <p className="text-xs text-destructive">
                      {errors.customer_user_id.message}
                    </p>
                  )}
                </div>
              </div>

              <input
                type="hidden"
                {...register('device_id', {
                  required: 'Device selection is required',
                })}
              />
              <input
                type="hidden"
                {...register('customer_user_id', {
                  required: 'Customer user selection is required',
                })}
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="border-0 bg-muted/50"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  className="flex items-center gap-2 border-muted-foreground/10 border-1"
                  variant='outline'
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Assignment
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-1 border-muted-foreground/20 shadow-sm bg-muted/40">
          <CardHeader className="space-y-2">
            <CardTitle className="flex items-center gap-2 text-xl">
              <ShieldCheck className="w-5 h-5 " />
              Assignment Preview
            </CardTitle>
            <CardDescription>
              Review linked details before saving the assignment.
            </CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-5">
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Device
              </p>
              <div className="rounded-md border-1 border-muted-foreground/20 bg-background p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground truncate">
                    {summaryDeviceName}
                  </span>
                  {selectedAssignment?.isActive && (
                    <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-100">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  IMEI: {summaryDeviceImei}
                </p>
                <p className="text-xs text-muted-foreground">
                  Assigned on: {summaryAssignedDate}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Account
              </p>
              <div className="rounded-md border-1 border-muted-foreground/20 bg-background p-3 space-y-2">
                <span className="text-sm font-semibold text-foreground">
                  {summaryAccountName}
                </span>
                <p className="text-xs text-muted-foreground">
                  {summaryAccountEmail || 'No account email available'}
                </p>
                
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Customer User
              </p>
              <div className="rounded-md border-1 border-muted-foreground/20 bg-background p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <UserCircle2 className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm font-semibold text-foreground">
                    {summaryCustomerName}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {summaryCustomerEmail || 'Awaiting customer selection'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {summaryCustomerPhone || 'No phone provided'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <BarcodeScannerDialog
        open={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        title="Scan device to select"
        onScan={(value) => {
          const match = findDeviceByScan(availableDevices, value);
          if (!match?.deviceId) {
            toast.error('No matching available device found for scanned code.');
            return;
          }
          handleDeviceChange(match.deviceId);
          toast.success('Device selected from scan.');
        }}
      />
    </div>
  );
};

export default AddCustomerDevices;